import { useRef, useState } from "react";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "@/lib/api";
import { CONTACT } from "@/config/site.config";
import FormField from "@/components/FormField";
import {
  HONEYPOT_FIELD,
  honeypotProps,
  isBot,
  LIMITS,
  runValidators,
  TIMER_FIELD,
  trimValues,
  validateEmail,
  validateMessage,
  validateName,
} from "@/lib/validation";

const RULES = {
  name: validateName,
  email: validateEmail,
  message: (v) => validateMessage(v, { required: true }),
};

const EMPTY = { name: "", email: "", message: "", [HONEYPOT_FIELD]: "" };

const FooterForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState("idle");
  // When the form was rendered — the server uses this to reject instant submissions.
  const startedAt = useRef(Date.now());

  const errors = runValidators(form, RULES);
  const field = (name) => ({
    value: form[name],
    onChange: (e) => setForm({ ...form, [name]: e.target.value }),
    onBlur: () => setTouched((t) => ({ ...t, [name]: true })),
    error: errors[name],
    show: submitted || touched[name],
  });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    if (Object.keys(errors).length) return;

    // Drop bot submissions silently — a specific error only tells them what to fix.
    if (isBot(form)) {
      setStatus("sent");
      return;
    }

    setStatus("sending");
    try {
      const { [HONEYPOT_FIELD]: _ignored, ...payload } = trimValues(form);
      await submitLead({ ...payload, source: "footer", [TIMER_FIELD]: startedAt.current });
      setStatus("sent");
    } catch {
      setStatus("idle");
      toast.error(`Could not send just now. Please email ${CONTACT.email} or WhatsApp ${CONTACT.phone}.`, { duration: 12000 });
    }
  };

  if (status === "sent") {
    return (
      <div className="rounded-2xl border border-volt/30 bg-volt/5 p-8" data-testid="footer-form-success">
        <p className="font-serif-display text-2xl italic text-volt">Received.</p>
        <p className="mt-2 text-sm text-white/60">We will write back within one working day.</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4" data-testid="footer-contact-form">
      <FormField
        label="Your name"
        placeholder="Your name"
        required
        maxLength={LIMITS.name.max}
        autoComplete="name"
        data-testid="footer-name-input"
        {...field("name")}
      />
      <FormField
        label="Email address"
        type="email"
        inputMode="email"
        placeholder="Email address"
        required
        maxLength={LIMITS.email.max}
        autoComplete="email"
        data-testid="footer-email-input"
        {...field("email")}
      />
      <FormField
        as="textarea"
        label="What are you building?"
        rows={3}
        placeholder="What are you building?"
        required
        maxLength={LIMITS.message.max}
        data-testid="footer-message-input"
        {...field("message")}
      />

      {/* Hidden from people, tempting to bots. */}
      <input
        {...honeypotProps}
        value={form[HONEYPOT_FIELD]}
        onChange={(e) => setForm({ ...form, [HONEYPOT_FIELD]: e.target.value })}
      />

      <button
        type="submit"
        disabled={status === "sending"}
        data-testid="footer-submit-button"
        className="group inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-ink transition-transform duration-300 hover:scale-105 disabled:opacity-60"
      >
        {status === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        Send message
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>
    </form>
  );
};

export default FooterForm;
